import {
  isValidNullapadInstanceSecretFormat,
  normalizeNullapadInstanceSecretInput,
  NULLAPAD_INSTANCE_SECRET_HEX_MAX_LEN,
  NULLAPAD_INSTANCE_SECRET_HEX_MIN_LEN,
} from "./nullapadInstanceSecret.js";

function nullapadRecipientIdValidationError(): string {
  return `Recipient id must be ${NULLAPAD_INSTANCE_SECRET_HEX_MIN_LEN}–${NULLAPAD_INSTANCE_SECRET_HEX_MAX_LEN} hexadecimal characters (NullaPad instance secret, 0-9 a-f).`;
}
import { iterUtf8StringSegmentsByMaxBytes } from "../text/utf8StringSegments.js";

const SHARE_ENDPOINT_STORAGE_KEY = "nullapad.shareEndpoint.v1";
const SHARE_LAST_RECIPIENT_STORAGE_KEY = "nullapad.shareLastRecipient.v1";

/** Last recipient instance secret used in Share-with (tab / editor context menus). */
export function getLastNullapadShareRecipientSecret(): string {
  try {
    const s = localStorage.getItem(SHARE_LAST_RECIPIENT_STORAGE_KEY)?.trim() ?? "";
    if (!isValidNullapadInstanceSecretFormat(s)) return "";
    return normalizeNullapadInstanceSecretInput(s);
  } catch {
    return "";
  }
}

/** Remember a valid recipient id for the next Share-with; empty string clears the saved value. */
export function saveLastNullapadShareRecipientSecret(secretHex: string): void {
  try {
    const n = normalizeNullapadInstanceSecretInput(secretHex);
    if (!n) {
      localStorage.removeItem(SHARE_LAST_RECIPIENT_STORAGE_KEY);
      return;
    }
    if (!isValidNullapadInstanceSecretFormat(n)) return;
    localStorage.setItem(SHARE_LAST_RECIPIENT_STORAGE_KEY, n);
  } catch {
    /* ignore */
  }
}

/** Max UTF-8 payload size per completed message (must match inbox server default). */
export const NULLAPAD_SHARE_MAX_MESSAGE_BYTES = 1024 * 1024;

/** Max raw bytes per single chunk POST body (same as max message here). */
export const NULLAPAD_SHARE_CHUNK_BYTES = NULLAPAD_SHARE_MAX_MESSAGE_BYTES;

/**
 * Interval for `GET /inbox` when a share endpoint is configured.
 * All NullaPads that use the **same instance secret** hit the same inbox slot — they share this cadence.
 */
export const NULLAPAD_SHARE_INBOX_POLL_INTERVAL_MS = 10_000;

/** Max assembled UTF-8 payload for Option A share sessions (default 50 MiB; server `MAX_SHARE_SESSION_BYTES`). */
export const NULLAPAD_SHARE_SESSION_MAX_BYTES = 50 * 1024 * 1024;

/** Segment size for large-share uploads (default 1 MiB UTF-8 bytes per segment; server `SHARE_SESSION_SEGMENT_BYTES`). */
export const NULLAPAD_SHARE_SESSION_SEGMENT_BYTES = NULLAPAD_SHARE_MAX_MESSAGE_BYTES;

export type NullapadShareProgress =
  | { phase: "begin" }
  | { phase: "chunk"; index: number; count: number }
  | { phase: "commit" };

/**
 * Base URL for `/begin`, `/chunk`, `/commit`, and `/inbox`.
 * Optional `localStorage` key `nullapad.shareEndpoint.v1` overrides; otherwise uses this page’s
 * `http(s)` origin. **Docker image from this repo:** nginx proxies those paths to the bundled inbox
 * on the same pod (no Ingress path rules). **Split deploy:** route those paths to your share Service
 * before the SPA catch-all.
 */
export function getSavedNullapadShareEndpoint(): string {
  try {
    const stored = localStorage.getItem(SHARE_ENDPOINT_STORAGE_KEY)?.trim() ?? "";
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return defaultShareBaseFromPageLocation();
}

/** Optional override for power users / tests (same key as `getSavedNullapadShareEndpoint` reads). */
export function saveNullapadShareEndpoint(url: string): void {
  try {
    localStorage.setItem(SHARE_ENDPOINT_STORAGE_KEY, url.trim());
  } catch {
    /* ignore */
  }
}

function defaultShareBaseFromPageLocation(): string {
  if (typeof window === "undefined") return "";
  try {
    const u = new URL(window.location.href);
    if (u.protocol === "http:" || u.protocol === "https:") return u.origin;
  } catch {
    /* ignore */
  }
  return "";
}

/** True for hostnames where any port is allowed (local inbox dev only). */
export function isShareDevLoopbackHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h === "::1";
}

/**
 * Production traffic should hit **only** the default web ports: **443** for `https:`,
 * **80** for `http:`. That keeps a single public listener (TLS terminator / reverse proxy).
 * Loopback hosts may use any port so a dev inbox can run on e.g. `http://127.0.0.1:8787`.
 */
export function assertShareEndpointUsesStandardWebPort(url: URL): void {
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("Share endpoint must use http:// or https://.");
  }
  if (isShareDevLoopbackHost(url.hostname)) return;
  const effectivePort = url.port === "" ? (url.protocol === "https:" ? "443" : "80") : url.port;
  const allowed = url.protocol === "https:" ? "443" : "80";
  if (effectivePort !== allowed) {
    throw new Error(
      `Share URL must use port ${allowed} for host "${url.hostname}" (https on 443 or http on 80). Put the inbox behind the same reverse proxy as the site, or use http://127.0.0.1 with any port for local development.`,
    );
  }
}

function normalizeShareApiBase(endpoint: string): string {
  const t = endpoint.trim().replace(/\/+$/, "");
  if (!t) throw new Error("Open NullaPad over http:// or https:// to use sharing.");
  let u: URL;
  try {
    u = new URL(t);
  } catch {
    throw new Error("Invalid share endpoint URL.");
  }
  assertShareEndpointUsesStandardWebPort(u);
  return t;
}

const MAX_SHARE_TAB_TITLE_CHARS = 512;

/**
 * Tab name hint for POST/GET share: basename of `filePath` when set, otherwise the tab `title`
 * (e.g. Untitled name). Clamped to {@link MAX_SHARE_TAB_TITLE_CHARS}.
 */
export function nullapadShareSuggestedTabTitle(doc: { title: string; filePath: string | null }): string {
  const fp = doc.filePath?.trim();
  if (fp) {
    const norm = fp.replace(/\\/g, "/");
    const parts = norm.split("/").filter((p) => p.length > 0);
    const leaf = parts[parts.length - 1]?.trim();
    if (leaf) return leaf.slice(0, MAX_SHARE_TAB_TITLE_CHARS);
  }
  return doc.title.trim().slice(0, MAX_SHARE_TAB_TITLE_CHARS);
}

async function sha256HexOfUtf8Text(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text);
  const h = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Shown when GET/POST body looks like index.html. Keep short: status bar truncates. */
const SHARE_ROUTE_HTML_HINT =
  "Same origin must serve JSON on /begin,/chunk,/commit,/inbox and /share-session/* (bundled image: nginx→inbox; else route API before SPA).";

function looksLikeHtmlDocument(raw: string): boolean {
  const t = raw.trimStart().slice(0, 32).toLowerCase();
  return t.startsWith("<!doctype") || t.startsWith("<html");
}

function parseShareJsonResponseBody(raw: string, pathLabel: string): unknown {
  if (looksLikeHtmlDocument(raw)) {
    throw new Error(`${pathLabel}: HTML not JSON (SPA?). ${SHARE_ROUTE_HTML_HINT}`);
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`${pathLabel}: expected JSON (${msg}).`);
  }
}

async function readErrorMessage(res: Response): Promise<string> {
  const raw = await res.text();
  if (looksLikeHtmlDocument(raw)) {
    return `HTTP ${res.status}: HTML not JSON. ${SHARE_ROUTE_HTML_HINT}`;
  }
  try {
    const j: unknown = JSON.parse(raw);
    if (j && typeof j === "object" && "error" in j && typeof (j as { error: unknown }).error === "string") {
      return (j as { error: string }).error;
    }
  } catch {
    /* ignore */
  }
  return `HTTP ${res.status} ${res.statusText}`;
}

/**
 * ## POST chunked share algorithm (browser → inbox server)
 *
 * 1. **Encode** the tab string as **UTF-8 bytes** (`TextEncoder`). Let `totalBytes` be the byte length.
 *    If `totalBytes` &gt; {@link NULLAPAD_SHARE_MAX_MESSAGE_BYTES}, abort (server inbox is capped at 1 MiB).
 *
 * 2. **Begin session** — `POST {apiBase}/begin` with JSON:
 *    `{ "recipientId": "<4–48 hex target secret>", "totalBytes": N, "tabTitle": "<optional>" }`.
 *    Server allocates an `uploadId`, records expected `totalBytes`, and returns
 *    `{ uploadId, chunkSizeBytes, maxInboxBytes, chunkCount }` where `chunkCount = ceil(totalBytes / chunkSizeBytes)`
 *    (0 when `totalBytes === 0`).
 *
 * 3. **Upload chunks** — for each index `i` in `0 .. chunkCount-1`, `POST {apiBase}/chunk` with:
 *    - Headers: `X-Nullapad-Recipient-Id`, `X-Nullapad-Upload-Id`, `X-Nullapad-Chunk-Index` (i),
 *      `X-Nullapad-Chunk-Count` (same as begin).
 *    - Body: **raw octets** for that slice (`application/octet-stream`), length exactly
 *      `min(chunkSizeBytes, totalBytes - i * chunkSizeBytes)`.
 *    Chunks may be sent in order (this client does sequential order).
 *
 * 4. **Commit** — `POST {apiBase}/commit` with JSON:
 *    `{ "uploadId", "recipientId", "totalBytes", "sha256Hex": "<hex of full UTF-8 body>" }`.
 *    Server concatenates chunk bodies in index order, verifies length and SHA-256, then stores **one message**
 *    (max 1 MiB) in the inbox keyed by `recipientId`, and drops the upload session.
 *
 * 5. **Empty tab** — if `totalBytes === 0`, call begin with `totalBytes: 0`, skip chunk posts, then commit
 *    (server accepts empty payload).
 *
 * **Reference server:** `server/nullapad-share-inbox.mjs`. On non-loopback hosts the share base URL must use
 * **443/https** or **80/http** only — see {@link assertShareEndpointUsesStandardWebPort}.
 *
 * ## Inbox poll (GET)
 *
 * Clients poll every {@link NULLAPAD_SHARE_INBOX_POLL_INTERVAL_MS} ms (same slot for matching `recipientId`).
 * `GET {apiBase}/inbox?recipientId=<4–48 hex>` returns `{ utf8, tabTitle?, byteLength?, fingerprint? }` when a message exists
 * (reference server includes `fingerprint` — use {@link resolveNullapadShareInboxFingerprint} for delete/dedupe),
 * or **404** when the slot is empty — see {@link fetchNullapadShareInboxMessage}.
 *
 * After the user accepts or declines, the client calls {@link deleteNullapadShareInboxMessage} (`DELETE`
 * same path) so the slot is empty for the next share and reloads do not replay the same payload.
 */
/** Inline body from GET /inbox (small messages). */
export type NullapadShareInboxSimpleMessage = {
  mode?: "simple";
  utf8: string;
  tabTitle: string | null;
  byteLength: number;
  /**
   * When present (reference inbox server), SHA-256 hex of the slot payload **as computed on the
   * server** — identical to what `DELETE /inbox?ifFingerprint=…` compares. Prefer this over
   * {@link computeNullapadShareInboxPayloadFingerprint} so Accept clears the slot even if client and
   * server would otherwise disagree on canonical UTF-8 edge cases.
   */
  fingerprint?: string;
};

/** Metadata when inbox holds a large assembled payload — fetch each segment via {@link downloadMultipartInboxAsUtf8}. */
export type NullapadShareInboxMultipartMeta = {
  mode: "multipart";
  shareSessionId: string;
  tabTitle: string | null;
  totalBytes: number;
  byteLength: number;
  segmentCount: number;
  segmentSizeBytes: number;
  /** Server slot fingerprint (same semantics as {@link NullapadShareInboxSimpleMessage.fingerprint}). */
  fingerprint?: string;
};

export type NullapadShareInboxMessage = NullapadShareInboxSimpleMessage | NullapadShareInboxMultipartMeta;

/**
 * SHA-256 hex fingerprint of an inbox payload (same algorithm as the reference inbox server’s
 * `ifFingerprint` check on `DELETE /inbox`).
 */
export async function computeNullapadShareInboxPayloadFingerprint(m: {
  utf8: string;
  tabTitle: string | null;
  byteLength: number;
}): Promise<string> {
  const canonical = `${m.byteLength}\0${m.tabTitle ?? ""}\0${m.utf8}`;
  const buf = new TextEncoder().encode(canonical);
  const h = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const SHARE_INBOX_FINGERPRINT_HEX_RE = /^[0-9a-f]{64}$/i;

/**
 * Fingerprint to use for dedupe, staging, and `DELETE …?ifFingerprint=` — prefers
 * {@link NullapadShareInboxSimpleMessage.fingerprint} from GET /inbox when the server sends it.
 */
export async function resolveNullapadShareInboxFingerprint(m: NullapadShareInboxSimpleMessage): Promise<string> {
  const fromServer = m.fingerprint?.trim().toLowerCase() ?? "";
  if (SHARE_INBOX_FINGERPRINT_HEX_RE.test(fromServer)) return fromServer;
  return computeNullapadShareInboxPayloadFingerprint(m);
}

/**
 * Fetches one completed message for this instance’s `recipientId` (the local NullaPad instance secret).
 * **404** from the inbox means the slot is empty → `null`.
 */
export async function fetchNullapadShareInboxMessage(opts: {
  endpoint: string;
  recipientId: string;
}): Promise<NullapadShareInboxMessage | null> {
  const apiBase = normalizeShareApiBase(opts.endpoint);
  const rid = opts.recipientId.trim();
  if (!isValidNullapadInstanceSecretFormat(rid)) {
    throw new Error(
      nullapadRecipientIdValidationError(),
    );
  }
  const res = await fetch(`${apiBase}/inbox?recipientId=${encodeURIComponent(rid)}`, {
    method: "GET",
    mode: "cors",
    cache: "no-store",
  });
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const j: unknown = parseShareJsonResponseBody(await res.text(), "GET /inbox");
  if (!j || typeof j !== "object") {
    throw new Error("Share inbox response invalid.");
  }
  const mode = "mode" in j && (j as { mode: unknown }).mode === "multipart" ? "multipart" : "simple";
  if (mode === "multipart") {
    const shareSessionId =
      "shareSessionId" in j && typeof (j as { shareSessionId: unknown }).shareSessionId === "string"
        ? (j as { shareSessionId: string }).shareSessionId
        : "";
    const tabTitle =
      "tabTitle" in j && (j as { tabTitle: unknown }).tabTitle === null
        ? null
        : "tabTitle" in j && typeof (j as { tabTitle: unknown }).tabTitle === "string"
          ? (j as { tabTitle: string }).tabTitle
          : null;
    const totalBytes =
      "totalBytes" in j && typeof (j as { totalBytes: unknown }).totalBytes === "number"
        ? (j as { totalBytes: number }).totalBytes
        : 0;
    const byteLength =
      "byteLength" in j && typeof (j as { byteLength: unknown }).byteLength === "number"
        ? (j as { byteLength: number }).byteLength
        : totalBytes;
    const segmentCount =
      "segmentCount" in j && typeof (j as { segmentCount: unknown }).segmentCount === "number"
        ? (j as { segmentCount: number }).segmentCount
        : 0;
    const segmentSizeBytes =
      "segmentSizeBytes" in j && typeof (j as { segmentSizeBytes: unknown }).segmentSizeBytes === "number"
        ? (j as { segmentSizeBytes: number }).segmentSizeBytes
        : NULLAPAD_SHARE_SESSION_SEGMENT_BYTES;
    if (!shareSessionId) {
      throw new Error("Share inbox multipart response missing shareSessionId.");
    }
    const fingerprint =
      "fingerprint" in j && typeof (j as { fingerprint: unknown }).fingerprint === "string"
        ? (j as { fingerprint: string }).fingerprint
        : undefined;
    return {
      mode: "multipart",
      shareSessionId,
      tabTitle,
      totalBytes,
      byteLength,
      segmentCount,
      segmentSizeBytes,
      ...(fingerprint !== undefined ? { fingerprint } : {}),
    };
  }
  if (!("utf8" in j) || typeof (j as { utf8: unknown }).utf8 !== "string") {
    throw new Error("Share inbox response missing utf8 string.");
  }
  const utf8 = (j as { utf8: string }).utf8;
  const tabTitle =
    "tabTitle" in j && (j as { tabTitle: unknown }).tabTitle === null
      ? null
      : "tabTitle" in j && typeof (j as { tabTitle: unknown }).tabTitle === "string"
        ? (j as { tabTitle: string }).tabTitle
        : null;
  const byteLength =
    "byteLength" in j && typeof (j as { byteLength: unknown }).byteLength === "number"
      ? (j as { byteLength: number }).byteLength
      : new TextEncoder().encode(utf8).byteLength;
  const fingerprint =
    "fingerprint" in j && typeof (j as { fingerprint: unknown }).fingerprint === "string"
      ? (j as { fingerprint: string }).fingerprint
      : undefined;
  return {
    utf8,
    tabTitle,
    byteLength,
    ...(fingerprint !== undefined ? { fingerprint } : {}),
  };
}

/**
 * Download a large inbox payload segment-by-segment (auto path on receiver; no per-segment UI).
 *
 * Segments are fixed **byte** ranges from the server (see `GET /share-session/segment`), not UTF-8
 * string chunks — the full payload must be **concatenated as bytes** before a single UTF-8 decode.
 */
export async function downloadMultipartInboxAsUtf8(opts: {
  endpoint: string;
  recipientId: string;
  meta: NullapadShareInboxMultipartMeta;
  onProgress?: (index1Based: number, segmentCount: number) => void;
}): Promise<NullapadShareInboxSimpleMessage> {
  const apiBase = normalizeShareApiBase(opts.endpoint);
  const rid = opts.recipientId.trim();
  if (!isValidNullapadInstanceSecretFormat(rid)) {
    throw new Error(
      nullapadRecipientIdValidationError(),
    );
  }
  const { shareSessionId, segmentCount, tabTitle, totalBytes } = opts.meta;
  /** Raw segment bodies — must not `TextDecoder` each chunk alone: UTF-8 code units can span MiB boundaries. */
  const segmentBytes: Uint8Array[] = [];
  for (let i = 0; i < segmentCount; i++) {
    opts.onProgress?.(i + 1, segmentCount);
    const url = `${apiBase}/share-session/segment?recipientId=${encodeURIComponent(rid)}&shareSessionId=${encodeURIComponent(shareSessionId)}&index=${i}`;
    const res = await fetch(url, { method: "GET", mode: "cors", cache: "no-store" });
    if (!res.ok) {
      throw new Error(await readErrorMessage(res));
    }
    segmentBytes.push(new Uint8Array(await res.arrayBuffer()));
  }
  let assembledLen = 0;
  for (const b of segmentBytes) assembledLen += b.length;
  if (assembledLen !== totalBytes) {
    throw new Error(
      `Share inbox: downloaded ${assembledLen} raw bytes across ${segmentCount} segments; expected ${totalBytes}.`,
    );
  }
  const assembled = new Uint8Array(assembledLen);
  let off = 0;
  for (const b of segmentBytes) {
    assembled.set(b, off);
    off += b.length;
  }
  const utf8 = new TextDecoder("utf-8", { fatal: true }).decode(assembled);
  const encLen = new TextEncoder().encode(utf8).byteLength;
  if (encLen !== totalBytes) {
    throw new Error(`Share inbox: assembled UTF-8 length ${encLen} does not match expected ${totalBytes}.`);
  }
  const fromMeta = opts.meta.fingerprint?.trim().toLowerCase() ?? "";
  return {
    utf8,
    tabTitle,
    byteLength: totalBytes,
    ...(SHARE_INBOX_FINGERPRINT_HEX_RE.test(fromMeta) ? { fingerprint: fromMeta } : {}),
  };
}

export type NullapadShareSessionProgress =
  | { phase: "begin" }
  | { phase: "segment"; index: number; count: number }
  | { phase: "finalize" };

/**
 * Share a tab that may exceed {@link NULLAPAD_SHARE_MAX_MESSAGE_BYTES}: Option A session with ~1 MiB UTF-8 segments.
 * Payloads ≤ {@link NULLAPAD_SHARE_MAX_MESSAGE_BYTES} use the legacy begin/chunk/commit path.
 */
export async function shareTabViaShareSession(opts: {
  endpoint: string;
  recipientId: string;
  tabText: string;
  tabTitle?: string;
  onProgress?: (p: NullapadShareSessionProgress) => void;
}): Promise<void> {
  const apiBase = normalizeShareApiBase(opts.endpoint);
  const rid = opts.recipientId.trim();
  if (!isValidNullapadInstanceSecretFormat(rid)) {
    throw new Error(
      nullapadRecipientIdValidationError(),
    );
  }
  const bytes = new TextEncoder().encode(opts.tabText);
  const totalBytes = bytes.byteLength;
  if (totalBytes <= NULLAPAD_SHARE_MAX_MESSAGE_BYTES) {
    await shareTabToNullapadViaPostChunked({
      endpoint: opts.endpoint,
      recipientId: rid,
      tabText: opts.tabText,
      tabTitle: opts.tabTitle,
    });
    return;
  }
  if (totalBytes > NULLAPAD_SHARE_SESSION_MAX_BYTES) {
    throw new Error(
      `Tab is ${totalBytes} bytes as UTF-8; max for large-share sessions is ${NULLAPAD_SHARE_SESSION_MAX_BYTES} bytes.`,
    );
  }
  const sha256Hex = await sha256HexOfUtf8Text(opts.tabText);
  opts.onProgress?.({ phase: "begin" });
  const beginRes = await fetch(`${apiBase}/share-session/begin`, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recipientId: rid,
      totalBytes,
      tabTitle: opts.tabTitle?.slice(0, 512) ?? null,
    }),
  });
  if (!beginRes.ok) {
    throw new Error(await readErrorMessage(beginRes));
  }
  const beginJson: unknown = parseShareJsonResponseBody(await beginRes.text(), "POST /share-session/begin");
  const shareSessionId =
    beginJson &&
    typeof beginJson === "object" &&
    "shareSessionId" in beginJson &&
    typeof (beginJson as { shareSessionId: unknown }).shareSessionId === "string"
      ? (beginJson as { shareSessionId: string }).shareSessionId
      : null;
  const segmentSizeBytes =
    beginJson &&
    typeof beginJson === "object" &&
    "segmentSizeBytes" in beginJson &&
    typeof (beginJson as { segmentSizeBytes: unknown }).segmentSizeBytes === "number"
      ? (beginJson as { segmentSizeBytes: number }).segmentSizeBytes
      : NULLAPAD_SHARE_SESSION_SEGMENT_BYTES;
  const segmentCount =
    beginJson &&
    typeof beginJson === "object" &&
    "segmentCount" in beginJson &&
    typeof (beginJson as { segmentCount: unknown }).segmentCount === "number"
      ? (beginJson as { segmentCount: number }).segmentCount
      : Math.ceil(totalBytes / segmentSizeBytes);
  if (!shareSessionId) {
    throw new Error("Share server did not return shareSessionId.");
  }
  const segments = [...iterUtf8StringSegmentsByMaxBytes(opts.tabText, segmentSizeBytes)];
  if (segments.length !== segmentCount) {
    throw new Error(`UTF-8 segment split produced ${segments.length} parts; server expects ${segmentCount}.`);
  }
  const enc = new TextEncoder();
  for (let i = 0; i < segmentCount; i++) {
    opts.onProgress?.({ phase: "segment", index: i + 1, count: segmentCount });
    const body = enc.encode(segments[i]!);
    const segRes = await fetch(`${apiBase}/share-session/segment`, {
      method: "POST",
      mode: "cors",
      cache: "no-store",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-Nullapad-Recipient-Id": rid,
        "X-Nullapad-Share-Session-Id": shareSessionId,
        "X-Nullapad-Segment-Index": String(i),
        "X-Nullapad-Segment-Count": String(segmentCount),
      },
      body,
    });
    if (!segRes.ok) {
      throw new Error(await readErrorMessage(segRes));
    }
    parseShareJsonResponseBody(await segRes.text(), "POST /share-session/segment");
  }
  opts.onProgress?.({ phase: "finalize" });
  const finRes = await fetch(`${apiBase}/share-session/finalize`, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      shareSessionId,
      recipientId: rid,
      totalBytes,
      sha256Hex,
    }),
  });
  if (!finRes.ok) {
    throw new Error(await readErrorMessage(finRes));
  }
  parseShareJsonResponseBody(await finRes.text(), "POST /share-session/finalize");
}

/**
 * Outcome of `DELETE /inbox`.
 *
 * - `"ok"`: the slot was deleted (or was already empty).
 * - `"superseded"`: the slot now holds a *different* payload than the one we fingerprinted (the
 *   sender raced in another share between our GET and our DELETE). We deliberately leave it intact
 *   so the next GET surfaces the newer payload instead of silently wiping it.
 */
export type DeleteNullapadShareInboxMessageResult = "ok" | "superseded";

/**
 * Clears the server inbox slot for this instance (call after accept/decline). Idempotent.
 *
 * **Pass `ifFingerprint`** for accept/decline. Without it, an unconditional DELETE will clobber a
 * message that arrived on the slot after the receiver's last GET but before this DELETE landed.
 * With it, the server only deletes when the current slot fingerprint still matches, and returns
 * `409` ("superseded") otherwise — which we forward as the `"superseded"` result.
 */
export async function deleteNullapadShareInboxMessage(opts: {
  endpoint: string;
  recipientId: string;
  ifFingerprint?: string;
}): Promise<DeleteNullapadShareInboxMessageResult> {
  const apiBase = normalizeShareApiBase(opts.endpoint);
  const rid = opts.recipientId.trim();
  if (!isValidNullapadInstanceSecretFormat(rid)) {
    throw new Error(
      nullapadRecipientIdValidationError(),
    );
  }
  const qs = new URLSearchParams({ recipientId: rid });
  const ifFp = opts.ifFingerprint?.trim().toLowerCase() ?? "";
  if (ifFp) qs.set("ifFingerprint", ifFp);
  const res = await fetch(`${apiBase}/inbox?${qs.toString()}`, {
    method: "DELETE",
    mode: "cors",
    cache: "no-store",
  });
  if (res.status === 204 || res.status === 404) return "ok";
  if (res.status === 409) return "superseded";
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  return "ok";
}

export async function shareTabToNullapadViaPostChunked(opts: {
  endpoint: string;
  recipientId: string;
  tabText: string;
  /** Optional tab title stored server-side with the message (dev inbox). */
  tabTitle?: string;
  onProgress?: (p: NullapadShareProgress) => void;
}): Promise<void> {
  const apiBase = normalizeShareApiBase(opts.endpoint);
  const rid = opts.recipientId.trim();
  if (!isValidNullapadInstanceSecretFormat(rid)) {
    throw new Error(
      nullapadRecipientIdValidationError(),
    );
  }

  const bytes = new TextEncoder().encode(opts.tabText);
  const totalBytes = bytes.byteLength;
  if (totalBytes > NULLAPAD_SHARE_MAX_MESSAGE_BYTES) {
    throw new Error(
      `Tab is ${totalBytes} bytes as UTF-8; max allowed is ${NULLAPAD_SHARE_MAX_MESSAGE_BYTES} (1 MiB).`,
    );
  }

  const sha256Hex = await sha256HexOfUtf8Text(opts.tabText);
  opts.onProgress?.({ phase: "begin" });

  const beginRes = await fetch(`${apiBase}/begin`, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recipientId: rid,
      totalBytes,
      tabTitle: opts.tabTitle?.slice(0, 512) ?? null,
    }),
  });
  if (!beginRes.ok) {
    throw new Error(await readErrorMessage(beginRes));
  }
  const beginJson: unknown = parseShareJsonResponseBody(await beginRes.text(), "POST /begin");
  const uploadId =
    beginJson &&
    typeof beginJson === "object" &&
    "uploadId" in beginJson &&
    typeof (beginJson as { uploadId: unknown }).uploadId === "string"
      ? (beginJson as { uploadId: string }).uploadId
      : null;
  const chunkSizeBytes =
    beginJson &&
    typeof beginJson === "object" &&
    "chunkSizeBytes" in beginJson &&
    typeof (beginJson as { chunkSizeBytes: unknown }).chunkSizeBytes === "number"
      ? (beginJson as { chunkSizeBytes: number }).chunkSizeBytes
      : NULLAPAD_SHARE_CHUNK_BYTES;
  const chunkCount =
    beginJson &&
    typeof beginJson === "object" &&
    "chunkCount" in beginJson &&
    typeof (beginJson as { chunkCount: unknown }).chunkCount === "number"
      ? (beginJson as { chunkCount: number }).chunkCount
      : Math.ceil(totalBytes / chunkSizeBytes);

  if (!uploadId) {
    throw new Error("Share server did not return uploadId.");
  }

  for (let i = 0; i < chunkCount; i++) {
    opts.onProgress?.({ phase: "chunk", index: i + 1, count: chunkCount });
    const start = i * chunkSizeBytes;
    const end = Math.min(start + chunkSizeBytes, totalBytes);
    const slice = bytes.subarray(start, end);
    const chunkRes = await fetch(`${apiBase}/chunk`, {
      method: "POST",
      mode: "cors",
      cache: "no-store",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-Nullapad-Recipient-Id": rid,
        "X-Nullapad-Upload-Id": uploadId,
        "X-Nullapad-Chunk-Index": String(i),
        "X-Nullapad-Chunk-Count": String(chunkCount),
      },
      body: slice,
    });
    if (!chunkRes.ok) {
      throw new Error(await readErrorMessage(chunkRes));
    }
    parseShareJsonResponseBody(await chunkRes.text(), "POST /chunk");
  }

  opts.onProgress?.({ phase: "commit" });
  const commitRes = await fetch(`${apiBase}/commit`, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      uploadId,
      recipientId: rid,
      totalBytes,
      sha256Hex,
    }),
  });
  if (!commitRes.ok) {
    throw new Error(await readErrorMessage(commitRes));
  }
  parseShareJsonResponseBody(await commitRes.text(), "POST /commit");
}
